// ============================================================================
// BANKING (Receipts + Receipt Verification Worksheet) HOOKS — ADR-0024.
// Mirrors useAccounts' generic list-hook factory (server-side pagination).
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { receiptService, receiptWorksheetService, bankStatementService } from 'api/services';
import type {
  PaginationMeta,
  ReceiptDto,
  ReceiptQueryParams,
  ReceiptAssignmentDto,
  ReceiptAssignmentQueryParams,
  BankStatementDto,
  BankStatementQueryParams,
} from 'api/types';

const DEFAULT_PAGINATION: PaginationMeta = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

// Generic list-hook factory (server-side pagination/filtering).
function useList<TDto, TParams>(
  fetcher: (params?: TParams) => Promise<{ data: TDto[]; pagination: PaginationMeta }>,
  params?: TParams,
) {
  const [data, setData] = useState<TDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>(DEFAULT_PAGINATION);

  const paramsKey = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetcher(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, pagination, refetch: fetchData };
}

// ---- Receipt register (entity-level capture, Leg 1) ----
export const useReceipts = (params?: ReceiptQueryParams) =>
  useList<ReceiptDto, ReceiptQueryParams>((p) => receiptService.getAll(p), params);

// ---- Receipt Verification Worksheet inbox (BU-level, Leg 2) ----
export const useReceiptAssignments = (params?: ReceiptAssignmentQueryParams) =>
  useList<ReceiptAssignmentDto, ReceiptAssignmentQueryParams>((p) => receiptWorksheetService.getAll(p), params);

// ---- Bank statement register (slice ④) ----
export const useBankStatements = (params?: BankStatementQueryParams) =>
  useList<BankStatementDto, BankStatementQueryParams>((p) => bankStatementService.getAll(p), params);
